import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import EmptyRows from "../../../../components/reusableComponent/EmptyRows";
import CustomDate from "../../../../components/common/CustomDate";
import TableModal from "../../../../components/reusableComponent/TableModal";
import {
  useGetTicketClickToExpandQuery,
  useTicketDashboardQuery,
  TicketClickToExpandParams,
} from "../../../../Services/dashboard/TicketDashboard";

export interface GroupedSlaClickToExpandConfig {
  graphName: string;
  categoryIdField: string;
  categoryIdParam: string;
  resolvedValue: string;
  breachedValue: string;
  /** Uses date/status_type/status_id params with dashboard date range */
  workflowMode?: boolean;
}

export interface GroupedSlaChartConfig {
  title: string;
  modalSubtitle: string;
  emptyLabel: string;
  sampleCategories: string[];
  colorPalette: { dark: string; light: string }[];
  categoryColors?: Record<string, { dark: string; light: string }>;
  apiArrayKeys: string[];
  nestedItemKeys: string[];
  categoryNameFields: string[];
  excludedRootKeys: string[];
  dailyGraphFields?: {
    resolvedKeys: string[];
    breachedKeys: string[];
    itemResolvedKeys?: string[];
    itemBreachedKeys?: string[];
  };
  clickToExpand?: GroupedSlaClickToExpandConfig;
}

interface IProps {
  data: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  workflowId?: string | null;
  config: GroupedSlaChartConfig;
  configOverrides?: Partial<
    Pick<
      GroupedSlaChartConfig,
      "sampleCategories" | "colorPalette" | "categoryColors"
    >
  > & {
    dailyCategoryNames?: string[];
    categoryClickMeta?: Record<
      string,
      { statusType: string; useStatusId?: boolean }
    >;
  };
}

interface CategorySeries {
  name: string;
  withinKey: string;
  breachKey: string;
  darkColor: string;
  lightColor: string;
}

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

const expandButtonStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
  color: "white",
  border: "none",
  borderRadius: "10px",
  padding: "12px 20px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 600,
  transition: "all 0.3s",
  boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const toYYYYMMDD = (d: string | Date): string => {
  const date = typeof d === "string" ? new Date(d) : d;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatChartDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatAxisDate = (rawDate: string, dataLength: number): string => {
  const date = parseYyyyMmDdToLocalDate(rawDate.slice(0, 10));
  if (Number.isNaN(date.getTime())) return rawDate;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  if (dataLength <= 7) {
    return `${day}/${month}/${date.getFullYear()}`;
  }
  return `${day}/${month}`;
};

const getXAxisProps = (rows: { date: string; rawDate: string }[]) => {
  const dataLength = rows.length;
  const useAngledLabels = dataLength > 7;

  return {
    dataKey: "date",
    tickFormatter: (value: string) => {
      const row = rows.find((r) => r.date === value);
      if (row?.rawDate) return formatAxisDate(row.rawDate, dataLength);
      return value;
    },
    tick: {
      fontSize: dataLength > 20 ? 9 : 10,
      fill: "#64748b",
      fontWeight: 500,
    },
    tickLine: false,
    axisLine: { stroke: "#cbd5e1" },
    interval: 0,
    angle: useAngledLabels ? -45 : 0,
    textAnchor: useAngledLabels ? ("end" as const) : ("middle" as const),
    height: useAngledLabels ? 56 : 40,
    tickMargin: 6,
  };
};

const getChartMargin = (dataLength: number) => ({
  top: 12,
  right: 28,
  left: 4,
  bottom: dataLength > 7 ? 48 : 28,
});

const getScrollWidth = (dataLength: number, isExpanded: boolean) => {
  if (!isExpanded || dataLength <= 14) return null;
  return Math.max(900, dataLength * 34);
};

const sanitizeKey = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "_");

const parseCount = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const getCategoryName = (row: any, fields: string[]) => {
  for (const field of fields) {
    const value = String(row?.[field] ?? "").trim();
    if (value) return value;
  }
  return "";
};

const extractRawRows = (data: any, apiArrayKeys: string[]): any[] => {
  if (!data) return [];
  for (const key of apiArrayKeys) {
    const candidate = data[key];
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }
  return [];
};

const buildFromNestedDaily = (
  rows: any[],
  nestedItemKeys: string[],
  categoryNameFields: string[],
) => {
  const categorySet = new Set<string>();
  const byDate = new Map<string, Record<string, number>>();

  rows.forEach((row) => {
    const rawDate =
      typeof row?.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(row.date)
        ? row.date.slice(0, 10)
        : toYYYYMMDD(row?.date ?? new Date());

    const items = nestedItemKeys
      .map((key) => row?.[key])
      .find((value) => Array.isArray(value));
    if (!Array.isArray(items)) return;

    if (!byDate.has(rawDate)) byDate.set(rawDate, {});

    items.forEach((itemRow: any) => {
      const categoryName = getCategoryName(itemRow, categoryNameFields);
      if (!categoryName) return;

      categorySet.add(categoryName);
      const withinKey = `${sanitizeKey(categoryName)}_within`;
      const breachKey = `${sanitizeKey(categoryName)}_breach`;
      const bucket = byDate.get(rawDate)!;
      bucket[withinKey] = getResolvedValue(itemRow, [
        "tag_sl_resolved",
        "workflow_sl_resolved",
        "disposition_sl_resolved",
        "within_sla",
        "within_sl",
        "within",
      ]);
      bucket[breachKey] = getBreachedValue(itemRow, [
        "tag_sl_breached",
        "workflow_sl_breached",
        "disposition_sl_breached",
        "breach",
        "breached_sla",
        "without_sla",
        "breached",
      ]);
    });
  });

  return { categorySet, byDate };
};

const buildFromFlatDaily = (rows: any[], categoryNameFields: string[]) => {
  const categorySet = new Set<string>();
  const byDate = new Map<string, Record<string, number>>();

  rows.forEach((row) => {
    const categoryName = getCategoryName(row, categoryNameFields);
    if (!categoryName) return;

    const rawDate =
      typeof row?.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(row.date)
        ? row.date.slice(0, 10)
        : toYYYYMMDD(row?.date ?? new Date());

    categorySet.add(categoryName);
    if (!byDate.has(rawDate)) byDate.set(rawDate, {});

    const withinKey = `${sanitizeKey(categoryName)}_within`;
    const breachKey = `${sanitizeKey(categoryName)}_breach`;
    const bucket = byDate.get(rawDate)!;
    bucket[withinKey] = parseCount(
      row?.within_sla ?? row?.within_sl ?? row?.within,
    );
    bucket[breachKey] = parseCount(
      row?.breach ?? row?.breached_sla ?? row?.without_sla ?? row?.breached,
    );
  });

  return { categorySet, byDate };
};

const buildFromCategoryKeyed = (
  data: Record<string, any[]>,
  categoryNameFields: string[],
) => {
  const categorySet = new Set<string>();
  const byDate = new Map<string, Record<string, number>>();

  Object.entries(data).forEach(([categoryName, entries]) => {
    if (!Array.isArray(entries)) return;
    categorySet.add(categoryName);

    entries.forEach((row) => {
      const rawDate =
        typeof row?.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(row.date)
          ? row.date.slice(0, 10)
          : toYYYYMMDD(row?.date ?? new Date());

      if (!byDate.has(rawDate)) byDate.set(rawDate, {});

      const withinKey = `${sanitizeKey(categoryName)}_within`;
      const breachKey = `${sanitizeKey(categoryName)}_breach`;
      const bucket = byDate.get(rawDate)!;
      bucket[withinKey] = parseCount(
        row?.within_sla ?? row?.within_sl ?? row?.within,
      );
      bucket[breachKey] = parseCount(
        row?.breach ?? row?.breached_sla ?? row?.without_sla ?? row?.breached,
      );
    });
  });

  return { categorySet, byDate };
};

const getResolvedValue = (row: any, keys: string[]) => {
  for (const key of keys) {
    if (row?.[key] != null) return parseCount(row[key]);
  }
  return parseCount(
    row?.within_sla ?? row?.within_sl ?? row?.withinSL ?? row?.within,
  );
};

const getBreachedValue = (row: any, keys: string[]) => {
  for (const key of keys) {
    if (row?.[key] != null) return parseCount(row[key]);
  }
  return parseCount(
    row?.breach ??
    row?.breached_sla ??
    row?.breach_count ??
    row?.without_sla ??
    row?.breached,
  );
};

const isDateKeyedSlGraphRow = (row: any): boolean => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  return Object.keys(row).some((key) => /^\d{4}-\d{2}-\d{2}$/.test(key));
};

const buildFromDateKeyedSlGraph = (
  rows: any[],
  dailyGraphFields: NonNullable<GroupedSlaChartConfig["dailyGraphFields"]>,
) => {
  const categorySet = new Set<string>();
  const byDate = new Map<string, Record<string, number>>();
  const itemResolvedKeys =
    dailyGraphFields.itemResolvedKeys ?? dailyGraphFields.resolvedKeys;
  const itemBreachedKeys =
    dailyGraphFields.itemBreachedKeys ?? dailyGraphFields.breachedKeys;

  rows.forEach((row) => {
    Object.entries(row).forEach(([dateKey, items]) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey) || !Array.isArray(items)) return;

      if (!byDate.has(dateKey)) byDate.set(dateKey, {});

      items.forEach((item: any) => {
        if (!item || typeof item !== "object") return;

        Object.entries(item).forEach(([categoryName, counts]) => {
          if (!categoryName || typeof counts !== "object" || counts == null) return;

          categorySet.add(categoryName);
          const withinKey = `${sanitizeKey(categoryName)}_within`;
          const breachKey = `${sanitizeKey(categoryName)}_breach`;
          const bucket = byDate.get(dateKey)!;
          bucket[withinKey] =
            parseCount(bucket[withinKey]) +
            getResolvedValue(counts, itemResolvedKeys);
          bucket[breachKey] =
            parseCount(bucket[breachKey]) +
            getBreachedValue(counts, itemBreachedKeys);
        });
      });
    });
  });

  return { categorySet, byDate };
};

const buildCategoryMetaMap = (
  data: any,
  apiArrayKeys: string[],
): Record<
  string,
  { statusType: string; statusId: string | number | null }
> => {
  const map: Record<
    string,
    { statusType: string; statusId: string | number | null }
  > = {};
  const rawRows = extractRawRows(data, apiArrayKeys);

  rawRows.forEach((row) => {
    if (!isDateKeyedSlGraphRow(row)) return;

    Object.entries(row).forEach(([, items]) => {
      if (!Array.isArray(items)) return;

      items.forEach((item: any) => {
        if (!item || typeof item !== "object") return;

        Object.entries(item).forEach(([categoryName, counts]) => {
          if (!categoryName || typeof counts !== "object" || counts == null) return;
          const statusType = String(
            (counts as Record<string, unknown>).status_type ?? "",
          ).trim();
          if (!statusType) return;

          const rawStatusId = (counts as Record<string, unknown>).status_id;
          map[categoryName] = {
            statusType,
            statusId:
              rawStatusId != null && rawStatusId !== ""
                ? (rawStatusId as string | number)
                : null,
          };
        });
      });
    });
  });

  return map;
};

const buildCategoryIdMap = (
  data: any,
  apiArrayKeys: string[],
  categoryIdField: string,
): Record<string, string | number> => {
  const map: Record<string, string | number> = {};
  const rawRows = extractRawRows(data, apiArrayKeys);

  rawRows.forEach((row) => {
    if (!isDateKeyedSlGraphRow(row)) return;

    Object.entries(row).forEach(([, items]) => {
      if (!Array.isArray(items)) return;

      items.forEach((item: any) => {
        if (!item || typeof item !== "object") return;

        Object.entries(item).forEach(([categoryName, counts]) => {
          if (!categoryName || typeof counts !== "object" || counts == null) return;
          const id = (counts as Record<string, unknown>)[categoryIdField];
          if (id != null && id !== "") {
            map[categoryName] = id as string | number;
          }
        });
      });
    });
  });

  return map;
};

const formatTicketCreated = (raw: unknown) => {
  if (!raw) return "—";
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
};

const TICKET_CLICK_TO_EXPAND_COLUMNS = [
  {
    label: "Ticket ID",
    render: (row: any) => row?.ticket_id ?? row?.id ?? "—",
  },
  { label: "Title", render: (row: any) => row?.title ?? "—" },
  { label: "Status Type", render: (row: any) => row?.status_type ?? "—" },
  {
    label: "Contact Name",
    render: (row: any) => row?.contact_name ?? "—",
  },
  {
    label: "Contact Email",
    render: (row: any) => row?.contact_email ?? "—",
  },
  {
    label: "Contact Phone",
    render: (row: any) => row?.contact_phone ?? "—",
  },
  {
    label: "Created",
    render: (row: any) => formatTicketCreated(row?.created),
  },
];

const buildFromDailySlGraph = (
  rows: any[],
  nestedItemKeys: string[],
  categoryNameFields: string[],
  dailyGraphFields: NonNullable<GroupedSlaChartConfig["dailyGraphFields"]>,
  categoryNames?: string[],
) => {
  const categorySet = new Set<string>();
  const byDate = new Map<string, Record<string, number>>();
  const itemResolvedKeys = dailyGraphFields.itemResolvedKeys ??
    dailyGraphFields.resolvedKeys;
  const itemBreachedKeys = dailyGraphFields.itemBreachedKeys ??
    dailyGraphFields.breachedKeys;

  rows.forEach((row) => {
    const rawDate =
      typeof row?.date === "string" && /^\d{4}-\d{2}-\d{2}/.test(row.date)
        ? row.date.slice(0, 10)
        : toYYYYMMDD(row?.date ?? new Date());

    const items = nestedItemKeys
      .map((key) => row?.[key])
      .find((value) => Array.isArray(value) && value.length > 0);

    if (!byDate.has(rawDate)) byDate.set(rawDate, {});

    if (Array.isArray(items)) {
      items.forEach((itemRow: any) => {
        const categoryName = getCategoryName(itemRow, categoryNameFields);
        if (!categoryName) return;

        categorySet.add(categoryName);
        const withinKey = `${sanitizeKey(categoryName)}_within`;
        const breachKey = `${sanitizeKey(categoryName)}_breach`;
        const bucket = byDate.get(rawDate)!;
        bucket[withinKey] = getResolvedValue(itemRow, itemResolvedKeys);
        bucket[breachKey] = getBreachedValue(itemRow, itemBreachedKeys);
      });
      return;
    }

    const names =
      categoryNames && categoryNames.length > 0 ? categoryNames : ["All"];

    names.forEach((categoryName) => {
      categorySet.add(categoryName);
      const withinKey = `${sanitizeKey(categoryName)}_within`;
      const breachKey = `${sanitizeKey(categoryName)}_breach`;
      const bucket = byDate.get(rawDate)!;

      if (names.length === 1) {
        bucket[withinKey] = getResolvedValue(row, dailyGraphFields.resolvedKeys);
        bucket[breachKey] = getBreachedValue(row, dailyGraphFields.breachedKeys);
      } else {
        bucket[withinKey] = 0;
        bucket[breachKey] = 0;
      }
    });
  });

  return { categorySet, byDate };
};

const buildChartModel = (
  data: any,
  config: GroupedSlaChartConfig,
  categoryNamesOverride?: string[],
) => {
  const rawRows = extractRawRows(data, config.apiArrayKeys);
  const hasApiSource = rawRows.length > 0;
  let parsed = {
    categorySet: new Set<string>(),
    byDate: new Map<string, Record<string, number>>(),
  };

  if (rawRows.length > 0) {
    const hasDateKeyedRows = rawRows.some(isDateKeyedSlGraphRow);
    const hasNestedItems = rawRows.some((row) =>
      config.nestedItemKeys.some(
        (key) => Array.isArray(row?.[key]) && row[key].length > 0,
      ),
    );

    if (hasDateKeyedRows && config.dailyGraphFields) {
      parsed = buildFromDateKeyedSlGraph(rawRows, config.dailyGraphFields);
    } else if (hasNestedItems) {
      parsed = buildFromNestedDaily(
        rawRows,
        config.nestedItemKeys,
        config.categoryNameFields,
      );
    } else if (config.dailyGraphFields) {
      parsed = buildFromDailySlGraph(
        rawRows,
        config.nestedItemKeys,
        config.categoryNameFields,
        config.dailyGraphFields,
        categoryNamesOverride,
      );
    } else {
      parsed = buildFromFlatDaily(rawRows, config.categoryNameFields);
    }
  } else if (data && typeof data === "object" && !Array.isArray(data)) {
    const categoryKeyed = Object.entries(data).find(
      ([key, value]) =>
        !config.excludedRootKeys.includes(key) &&
        Array.isArray(value) &&
        value.some((item) => item?.date),
    );

    if (categoryKeyed) {
      const keyedData: Record<string, any[]> = {};
      keyedData[categoryKeyed[0]] = categoryKeyed[1] as any[];
      parsed = buildFromCategoryKeyed(keyedData, config.categoryNameFields);
    }
  }

  const categories = Array.from(parsed.categorySet);
  const chartData = Array.from(parsed.byDate.entries())
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([rawDate, values]) => ({
      rawDate,
      date: formatChartDate(rawDate),
      ...values,
    }));

  const categorySeries: CategorySeries[] = categories.map((category, index) => {
    const palette =
      config.categoryColors?.[category] ??
      config.colorPalette[index % config.colorPalette.length];
    return {
      name: category,
      withinKey: `${sanitizeKey(category)}_within`,
      breachKey: `${sanitizeKey(category)}_breach`,
      darkColor: palette.dark,
      lightColor: palette.light,
    };
  });

  const hasValues = chartData.some((row) =>
    categorySeries.some(
      (series) =>
        parseCount(row[series.withinKey]) > 0 ||
        parseCount(row[series.breachKey]) > 0,
    ),
  );

  const hasChartStructure = chartData.length > 0 && categorySeries.length > 0;

  return { chartData, categorySeries, hasValues, hasChartStructure, hasApiSource };
};

const CategoryLegend = ({
  categorySeries,
  hiddenCategories,
  onToggle,
}: {
  categorySeries: CategorySeries[];
  hiddenCategories: string[];
  onToggle: (name: string) => void;
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "16px 12px 4px",
    }}
  >
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "12px",
        justifyContent: "center",
      }}
    >
      {categorySeries.map((series) => {
        const isHidden = hiddenCategories.includes(series.name);
        return (
          <button
            key={series.name}
            type="button"
            onClick={() => onToggle(series.name)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              borderRadius: "8px",
              border: isHidden
                ? "1px solid #e2e8f0"
                : `1px solid ${series.darkColor}40`,
              backgroundColor: isHidden ? "#f8fafc" : "#ffffff",
              cursor: "pointer",
              opacity: isHidden ? 0.55 : 1,
            }}
          >
            <div style={{ display: "flex", gap: "3px" }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: series.darkColor,
                }}
              />
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: series.lightColor,
                }}
              />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
              {series.name}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const CustomTooltip = ({
  active,
  payload,
  label,
  coordinate,
  hoveredCell,
  chartRootRef,
}: any) => {
  if (!active || !payload?.length) return null;

  const entry = hoveredCell?.dataKey
    ? payload.find((item: any) => item?.dataKey === hoveredCell.dataKey)
    : payload.find((item: any) => parseCount(item?.value) > 0) ?? payload[0];

  if (!entry || parseCount(entry?.value) <= 0) return null;

  const displayDate =
    label ??
    entry?.payload?.date ??
    (entry?.payload?.rawDate ? formatChartDate(entry.payload.rawDate) : "");

  const tooltipBody = (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "10px 12px",
        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
        fontSize: 12,
        minWidth: 160,
        maxWidth: 260,
        whiteSpace: "nowrap",
      }}
    >
      {displayDate ? (
        <div
          style={{
            fontWeight: 700,
            color: "#1e293b",
            marginBottom: 8,
            paddingBottom: 6,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          Date: {displayDate}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "#475569",
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor: entry.color,
            flexShrink: 0,
          }}
        />
        <span>{entry.name}</span>
        <strong style={{ marginLeft: "auto", color: "#1e293b", paddingLeft: 12 }}>
          {entry.value}
        </strong>
      </div>
    </div>
  );

  /** Portal + edge flip so last-date bars are not clipped by chart overflow. */
  const rootEl = chartRootRef?.current as HTMLElement | null | undefined;
  const rect = rootEl?.getBoundingClientRect();
  if (
    typeof document !== "undefined" &&
    rect &&
    coordinate?.x != null &&
    coordinate?.y != null
  ) {
    const tooltipWidth = 220;
    const tooltipHeight = 72;
    const offset = 14;
    let left = rect.left + coordinate.x + offset;
    let top = rect.top + coordinate.y - tooltipHeight / 2;

    if (left + tooltipWidth > window.innerWidth - 12) {
      left = rect.left + coordinate.x - tooltipWidth - offset;
    }
    if (left < 12) left = 12;
    if (top < 12) top = 12;
    if (top + tooltipHeight > window.innerHeight - 12) {
      top = window.innerHeight - tooltipHeight - 12;
    }

    return createPortal(
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 100000,
          pointerEvents: "none",
        }}
      >
        {tooltipBody}
      </div>,
      document.body,
    );
  }

  return tooltipBody;
};

const GroupedSlaBarChart: React.FC<IProps> = ({
  data,
  startDate,
  endDate,
  teamId,
  workflowId,
  config,
  configOverrides,
}) => {
  const navigate = useNavigate();
  const chartRootRef = useRef<HTMLDivElement | null>(null);
  const effectiveConfig = useMemo(
    () => ({
      ...config,
      ...(configOverrides?.sampleCategories?.length
        ? { sampleCategories: configOverrides.sampleCategories }
        : {}),
      ...(configOverrides?.colorPalette?.length
        ? { colorPalette: configOverrides.colorPalette }
        : {}),
      ...(configOverrides?.categoryColors
        ? { categoryColors: configOverrides.categoryColors }
        : {}),
    }),
    [config, configOverrides],
  );

  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);
  const [hoveredCell, setHoveredCell] = useState<{
    index: number;
    dataKey: string;
  } | null>(null);
  const [fullscreenChart, setFullscreenChart] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);
  const [selectedBarClick, setSelectedBarClick] = useState<{
    selectedDate: string;
    categoryId?: string;
    categoryName: string;
    statusType?: string;
    statusId?: string | number | null;
    value: string;
    valueLabel: string;
  } | null>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange],
  );

  const { data: modalPerformanceData } = useTicketDashboardQuery(
    {
      dashboardType: "performance",
      selectedTeam: teamId ?? "",
      selectedWorkFlow: workflowId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    },
  );

  const { chartData, categorySeries, hasChartStructure } = useMemo(
    () =>
      buildChartModel(
        data,
        effectiveConfig,
        configOverrides?.dailyCategoryNames,
      ),
    [data, effectiveConfig, configOverrides?.dailyCategoryNames],
  );

  const modalChartSource = useMemo(
    () =>
      fullscreenChart && modalPerformanceData != null
        ? modalPerformanceData
        : data,
    [fullscreenChart, modalPerformanceData, data],
  );

  const {
    chartData: modalChartData,
    categorySeries: modalCategorySeries,
    hasChartStructure: modalHasChartStructure,
  } = useMemo(
    () =>
      buildChartModel(
        modalChartSource,
        effectiveConfig,
        configOverrides?.dailyCategoryNames,
      ),
    [modalChartSource, effectiveConfig, configOverrides?.dailyCategoryNames],
  );

  const categoryIdMap = useMemo(() => {
    const clickConfig = effectiveConfig.clickToExpand;
    if (!clickConfig || clickConfig.workflowMode) return {};
    return buildCategoryIdMap(
      data,
      effectiveConfig.apiArrayKeys,
      clickConfig.categoryIdField,
    );
  }, [data, effectiveConfig.apiArrayKeys, effectiveConfig.clickToExpand]);

  const categoryMetaMap = useMemo(() => {
    const clickConfig = effectiveConfig.clickToExpand;
    if (!clickConfig?.workflowMode) return {};
    return buildCategoryMetaMap(data, effectiveConfig.apiArrayKeys);
  }, [data, effectiveConfig.apiArrayKeys, effectiveConfig.clickToExpand]);

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate ?? "";
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate ?? "";

  const clickToExpandParams = useMemo((): TicketClickToExpandParams | null => {
    const clickConfig = effectiveConfig.clickToExpand;
    if (!tableModalOpen || !selectedBarClick || !teamId || !clickConfig) {
      return null;
    }

    if (clickConfig.workflowMode) {
      if (!expandStart || !expandEnd || !selectedBarClick.statusType) {
        return null;
      }

      const params: TicketClickToExpandParams = {
        graph_name: clickConfig.graphName,
        start_date: expandStart,
        end_date: expandEnd,
        team_id: teamId,
        date: selectedBarClick.selectedDate,
        status_type: selectedBarClick.statusType,
        page: clickToExpandPage,
        page_size: 20,
        ...(workflowId ? { workflow_id: workflowId } : {}),
        ...(selectedBarClick.statusType === "wip" &&
          selectedBarClick.statusId != null &&
          selectedBarClick.statusId !== ""
          ? { status_id: selectedBarClick.statusId }
          : {}),
      };

      return params;
    }

    return {
      graph_name: clickConfig.graphName,
      value: selectedBarClick.value,
      start_date: selectedBarClick.selectedDate,
      end_date: selectedBarClick.selectedDate,
      team_id: teamId,
      page: clickToExpandPage,
      page_size: 20,
      selected_date: selectedBarClick.selectedDate,
      ...(workflowId ? { workflow_id: workflowId } : {}),
      ...(clickConfig.categoryIdParam === "tag_id" && selectedBarClick.categoryId
        ? { tag_id: selectedBarClick.categoryId }
        : {}),
    };
  }, [
    tableModalOpen,
    selectedBarClick,
    teamId,
    workflowId,
    clickToExpandPage,
    effectiveConfig.clickToExpand,
    expandStart,
    expandEnd,
  ]);

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 20;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [
    selectedBarClick?.categoryId,
    selectedBarClick?.selectedDate,
    selectedBarClick?.value,
    selectedBarClick?.statusType,
    selectedBarClick?.statusId,
  ]);

  const visibleSeries = categorySeries.filter(
    (series) => !hiddenCategories.includes(series.name),
  );

  const visibleModalSeries = modalCategorySeries.filter(
    (series) => !hiddenCategories.includes(series.name),
  );

  useEffect(() => {
    if (tableModalOpen) setHoveredCell(null);
  }, [tableModalOpen]);

  const getCellStyle = useCallback(
    (
      index: number,
      dataKey: string,
      baseColor: string,
      row: any,
      clickable: boolean,
    ) => {
      const isHovered =
        hoveredCell?.index === index && hoveredCell?.dataKey === dataKey;
      const hasValue = parseCount(row?.[dataKey]) > 0;

      return {
        fill: baseColor,
        fillOpacity: isHovered && hasValue ? 0.72 : 1,
        stroke: "none",
        strokeWidth: 0,
        style: {
          cursor: clickable && hasValue ? "pointer" : "default",
          outline: "none",
        },
      };
    },
    [hoveredCell],
  );

  const handleBarCellClick = useCallback(
    (
      rowIndex: number,
      series: CategorySeries,
      dataKey: string,
      rows: any[],
    ) => {
      const clickConfig = effectiveConfig.clickToExpand;
      if (!clickConfig || !teamId) return;

      const row = rows[rowIndex];
      if (!row || parseCount(row[dataKey]) <= 0) return;

      const rawDate = row.rawDate;
      if (!rawDate) return;

      const isBreach = dataKey === series.breachKey;
      const apiValue = isBreach
        ? clickConfig.breachedValue
        : clickConfig.resolvedValue;
      const valueLabel = isBreach ? "Breach SL" : "Within SL";

      if (clickConfig.workflowMode) {
        const graphMeta = categoryMetaMap[series.name];
        const stageMeta = configOverrides?.categoryClickMeta?.[series.name];
        const statusType = stageMeta?.statusType ?? graphMeta?.statusType;
        if (!statusType) return;

        const includeStatusId =
          stageMeta?.useStatusId ??
          (statusType === "wip" && graphMeta?.statusId != null);

        setSelectedBarClick({
          selectedDate: rawDate,
          categoryName: series.name,
          statusType,
          statusId: includeStatusId ? graphMeta?.statusId ?? null : null,
          value: apiValue,
          valueLabel,
        });
        setHoveredCell(null);
        setTableModalOpen(true);
        setClickToExpandPage(1);
        return;
      }

      const categoryId = categoryIdMap[series.name];
      if (categoryId == null) return;

      setSelectedBarClick({
        selectedDate: rawDate,
        categoryId: String(categoryId),
        categoryName: series.name,
        value: apiValue,
        valueLabel,
      });
      setHoveredCell(null);
      setTableModalOpen(true);
      setClickToExpandPage(1);
    },
    [categoryIdMap, categoryMetaMap, configOverrides?.categoryClickMeta, effectiveConfig.clickToExpand, teamId],
  );

  const handleCloseTableModal = () => {
    setTableModalOpen(false);
    setSelectedBarClick(null);
    setClickToExpandPage(1);
  };

  const clickToExpandEnabled = Boolean(effectiveConfig.clickToExpand && teamId);

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const CellContainer = Cell as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;
  const TooltipContainer = Tooltip as any;
  const CartesianGridContainer = CartesianGrid as any;
  const LegendContainer = Legend as any;

  const toggleCategory = (name: string) => {
    setHiddenCategories((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name],
    );
  };

  const clearHoveredCell = useCallback(() => {
    setHoveredCell(null);
  }, []);

  const renderChart = (
    rows: any[],
    series: CategorySeries[],
    visibleItems: CategorySeries[],
    height: number | string,
    isExpanded = false,
  ) => {
    const scrollWidth = getScrollWidth(rows.length, isExpanded);
    const chartNode = (
      <div ref={chartRootRef} style={{ width: "100%", height: "100%" }}>
        <ResponsiveChartContainer width="100%" height={height}>
          <BarChartContainer
            data={rows}
            margin={getChartMargin(rows.length)}
            barCategoryGap="18%"
            barGap={4}
            onMouseLeave={clearHoveredCell}
          >
            <CartesianGridContainer
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxisContainer {...getXAxisProps(rows)} />
            <YAxisContainer
              tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }}
              tickLine={false}
              axisLine={{ stroke: "#cbd5e1" }}
              allowDecimals={false}
            />
            <TooltipContainer
              content={
                <CustomTooltip
                  hoveredCell={hoveredCell}
                  chartRootRef={chartRootRef}
                />
              }
              shared={true}
              cursor={false}
              isAnimationActive={false}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{
                pointerEvents: "none",
                visibility: "hidden",
                zIndex: -1,
              }}
              labelFormatter={(value: string) => value}
            />
            <LegendContainer
              verticalAlign="bottom"
              content={
                <CategoryLegend
                  categorySeries={series}
                  hiddenCategories={hiddenCategories}
                  onToggle={toggleCategory}
                />
              }
            />

            {visibleItems.map((series) => (
              <React.Fragment key={series.name}>
                <BarContainer
                  dataKey={series.withinKey}
                  name={`${series.name} within SL`}
                  stackId={series.name}
                  fill={series.darkColor}
                  radius={[0, 0, 0, 0]}
                  maxBarSize={18}
                  isAnimationActive={false}
                  activeBar={false}
                >
                  {rows.map((row, index) => (
                    <CellContainer
                      key={`${series.withinKey}-${index}`}
                      {...getCellStyle(
                        index,
                        series.withinKey,
                        series.darkColor,
                        row,
                        clickToExpandEnabled,
                      )}
                      onMouseEnter={() =>
                        setHoveredCell({ index, dataKey: series.withinKey })
                      }
                      onClick={() =>
                        handleBarCellClick(index, series, series.withinKey, rows)
                      }
                    />
                  ))}
                </BarContainer>
                <BarContainer
                  dataKey={series.breachKey}
                  name={`${series.name} breach`}
                  stackId={series.name}
                  fill={series.lightColor}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={18}
                  isAnimationActive={false}
                  activeBar={false}
                >
                  {rows.map((row, index) => (
                    <CellContainer
                      key={`${series.breachKey}-${index}`}
                      {...getCellStyle(
                        index,
                        series.breachKey,
                        series.lightColor,
                        row,
                        clickToExpandEnabled,
                      )}
                      onMouseEnter={() =>
                        setHoveredCell({ index, dataKey: series.breachKey })
                      }
                      onClick={() =>
                        handleBarCellClick(index, series, series.breachKey, rows)
                      }
                    />
                  ))}
                </BarContainer>
              </React.Fragment>
            ))}
          </BarChartContainer>
        </ResponsiveChartContainer>
      </div>
    );

    if (scrollWidth) {
      return (
        <div
          style={{
            width: "100%",
            height,
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ minWidth: scrollWidth, height: "100%" }}>{chartNode}</div>
        </div>
      );
    }

    return chartNode;
  };

  return (
    <>
      <div className="card card-flush border-0 mb-2">
        <div className="d-flex align-items-center justify-content-between card-header w-100">
          <div>
            <span className="fs-2 fw-bold text-gray-800 me-2 lh-1 ls-n2">
              {effectiveConfig.title}
            </span>
            {effectiveConfig.modalSubtitle ? (
              <div
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginTop: "4px",
                }}
              >
                {effectiveConfig.modalSubtitle}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setFullscreenChart(true)}
            style={expandButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(99, 102, 241, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(99, 102, 241, 0.3)";
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
            Expand View
          </button>
        </div>

        <div className="card-body pt-1 px-3 pb-3">
          {!hasChartStructure ? (
            <div
              style={{
                height: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmptyRows value={effectiveConfig.emptyLabel} />
            </div>
          ) : (
            renderChart(chartData, categorySeries, visibleSeries, 440)
          )}
        </div>
      </div>

      {fullscreenChart ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            zIndex: 9999,
            padding: "24px",
            overflow: "auto",
          }}
          onClick={() => setFullscreenChart(false)}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              width: "95%",
              maxWidth: "1600px",
              margin: "0 auto",
              padding: "32px",
              position: "relative",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "28px",
                paddingBottom: "20px",
                borderBottom: "2px solid #e2e8f0",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div>
                <h2 style={{ margin: 0, color: "#1e293b", fontWeight: 700 }}>
                  {effectiveConfig.title}
                </h2>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    color: "#64748b",
                    fontSize: "14px",
                  }}
                >
                  {effectiveConfig.modalSubtitle}
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                  flexShrink: 0,
                }}
              >
                <div style={{ position: "relative", zIndex: 10002 }}>
                  <CustomDate
                    dateRange={modalDateRange}
                    setDateRange={setModalDateRange}
                    unrestricted
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setFullscreenChart(false)}
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    fontSize: "24px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s",
                    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div
              style={{
                width: "100%",
                height: "560px",
                backgroundColor: "#fafbfc",
                padding: "24px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              }}
            >
              {modalHasChartStructure ? (
                renderChart(
                  modalChartData,
                  modalCategorySeries,
                  visibleModalSeries,
                  "100%",
                  true,
                )
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EmptyRows value={effectiveConfig.emptyLabel} />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseTableModal}
        title={
          selectedBarClick
            ? `${selectedBarClick.categoryName} – ${selectedBarClick.valueLabel} (${formatChartDate(selectedBarClick.selectedDate)})`
            : "Tickets"
        }
        contentLabel={`${effectiveConfig.title} click-to-expand`}
        emptyMessage="No tickets found for this selection."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseTableModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={TICKET_CLICK_TO_EXPAND_COLUMNS}
      />
    </>
  );
};

export default GroupedSlaBarChart;
